// A PARTY LIST THAT RAN PAST ITS PAGE — said once, for every screen that lists parties or answers "no party by that name".
//
// GET /api/parties answers `more: true` when the roster went past the page it was asked for (bff/src/routes/parties.ts, Mechanisms
// 23 Oct 2026): the rows are the FIRST of them, alphabetically, not all. Before the flag the list ended silently — the desk has 134
// active parties and the endpoint's default page is 100 — and a short list looks exactly like a whole one. A list that is cut says so,
// and nothing says "no parties" or "nothing to convert" over one (design system Appendix B: a partial read is not an empty one).

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { PartyListAnswer } from './api';

/** True when a party-list answer says the roster went past its page. `=== true`: an older server sends no flag, and then there is nothing to say. */
export function rosterCut(answer: { more?: boolean } | null | undefined): boolean {
  return answer?.more === true;
}

/** The line under a cut list. `way` is how the operator reaches the rest: `search` — a search box on the screen that asks the books —
 *  or `type` — a picker whose text is sent to the books. */
export function cutRosterWords(shown: number, way: 'search' | 'type'): string {
  const rest = way === 'type' ? 'type to narrow' : 'search for the rest';
  return shown > 0 ? `Showing the first ${shown.toLocaleString('en-US')} — ${rest}.` : `Nobody in the first page — ${rest}.`;
}

/**
 * WHAT A PARTY SEARCH ASKS, from the text and from what the three-character answer said. Pure, so the rule can be tested without typing.
 *
 * The pickers key their search by the FIRST THREE CHARACTERS of the text and ask the books for exactly those three: the answer is the whole
 * roster of that prefix, and typing more only filters it on the screen — cheap, because each ask reads the ledger's balances, and right
 * whatever the text did before (a name pasted whole, then edited, asks nothing and still finds what the prefix holds). When that answer
 * was CUT (`more`), the rest of its matches can be reached only by asking again with more text: past three characters, and only then, the
 * whole text is asked for under a key of its own, so the next keystroke asks.
 */
export function partySearchPlan(text: string, headAnswer: { more?: boolean } | null | undefined): {
  prefix: string; headQ: string | undefined; wide: boolean; wholeKey: string; wholeQ: string | undefined;
} {
  const prefix = text.slice(0, 3);
  return {
    prefix, headQ: prefix.trim() || undefined,
    wide: text.length > 3 && rosterCut(headAnswer),
    wholeKey: text.trim().toLowerCase(), wholeQ: text.trim() || undefined,
  };
}

/**
 * A party search as a query: the prefix's answer always, and — while that answer is cut and the text is past three characters — the whole
 * text's. Both observers stay mounted, so the prefix's cut state is not garbage-collected while the whole text is asked. The whole text's
 * query answers for ITSELF: until it has, the search is WAITING — rows of another text's answer (the prefix's, an earlier whole text's) are
 * never shown as the answer to this one, and a read that has not succeeded is not "known" (design system Appendix B). `ask(q)` is the
 * caller's own call, `base` its key's head; `useQueryImpl` is the seam the tests use to read what is asked without a renderer.
 * (Reviews of 23 Oct 2026: "type to narrow" had to be true; a pasted name must not stall the prefix; no placeholder rows.)
 */
export function usePartySearch(o: {
  base: readonly unknown[]; text: string; ask: (q: string | undefined) => Promise<PartyListAnswer>; enabled?: boolean;
}, useQueryImpl: typeof useQuery = useQuery): UseQueryResult<PartyListAnswer> {
  const enabled = o.enabled ?? true;
  const prefix = o.text.slice(0, 3);
  const head = useQueryImpl({ queryKey: [...o.base, prefix], queryFn: () => o.ask(prefix.trim() || undefined), enabled });
  const plan = partySearchPlan(o.text, head.data);
  const whole = useQueryImpl({ queryKey: [...o.base, prefix, plan.wholeKey], queryFn: () => o.ask(plan.wholeQ), enabled: enabled && plan.wide });
  return plan.wide ? whole : head;
}
